<template>
    <div class="c-brands-page">
        <SearchProductControl :category-ids="getDefaultCategoryIds"
                              :brand-name="brandName"
                              :search-title="searchTitleComputed"
                              :page-data-url="pageDataUrl"
                              :search-result-callback="updateBrandPageMetaData"
                              :page-data-result-callback="onBrandContentPageLoad"
                              :allow-search="allowSearch"
                              :show-blocks-and-products="showBlocksAndProducts"
                              :hide-left-menu="hideLeftMenu"
                              render-meta-data
                              track-page-view
                              use-flattened-category-tree
                              reactive-categories
                              context-key="brands">
            <template #headline="{ searchResult }">
                <h1 v-if="brandSubContent" class="leading-none">
                    {{ brandSubHeadline }} <span v-if="searchResult" class="text-gray-800">({{ $dictionary.localeNumber(searchResult.totalHits) }})</span>
                </h1>
                <h1 v-else-if="searchResult" class="leading-none">
                    {{ searchResult.brandName }} <span class="text-gray-800">({{ $dictionary.localeNumber(searchResult.totalHits) }})</span>
                </h1>
                <h1 v-else class="leading-none capitalize">
                    {{ brandName }}
                </h1>
            </template>
            <template #navigation="{ menuItems, searchResult }">
                <CategoryFacets v-if="menuItems.length > 0"
                                :menu-items="menuItems"
                                :title="(brandSubContent && brandSubHeadline) || (searchResult && searchResult.brandName) || (brandNameComputed || brandName)"
                                :total-hits="(searchResult && searchResult.totalHits) || 0"/>
            </template>
        </SearchProductControl>

        <div class="o-container mt-40 md:flex">
            <div class="w-1/5"/>
            <BrandCategoriesLinks class="md:w-4/5" :brand-name="brandNameComputed" :active-brand-category="brandSubContent"/>
        </div>
    </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref, computed, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router/composables';
import urlHelper from '@/core/urlHelper.service';
import { SearchViewModel } from '@/types/apiServerContract';
import dictionaryService, { NamedLabelArgument } from '@/core/translation/dictionary.service';
import domService from '@/core/dom/dom.service';
import { PageData } from '@/types/contentServerContract';
import botService from '@/project/site/bot.service';
import serverContextService from '@/core/serverContext.service';

const BrandCategoriesLinks = defineAsyncComponent(() => import('@/project/product/product-details/BrandCategoriesLinks.vue'));
const CategoryFacets = defineAsyncComponent(() => import('@/project/search/facets/CategoryFacets.vue'));
const SearchProductControl = defineAsyncComponent(() => import('@/project/search/SearchProductControl.vue'));

const props = defineProps({
    brandName: {
        type: String,
        required: true
    },
    brandSubContent: {
        type: String,
        default: null
    }
});

const route = useRoute();

const pageDataUrl = ref<string | null>(null);
const pageData = ref<PageData | null>(null);
const allowSearch = ref<boolean>(true);
const brandSubHeadline = ref<string | null>(null);
const categoryName = ref<string | null>(null);

function updateBrandPageMetaData(searchResult: SearchViewModel) {
    handleSearchResultCallback(searchResult);
}

async function handleSearchResultCallback(searchResult: SearchViewModel) {
    const totalHits = dictionaryService.seofyNumbers(searchResult.totalHits);
    const totalResults = totalHits !== undefined && totalHits !== 0 ? totalHits.toString(10) : undefined;
    const labelValues: NamedLabelArgument = {
        brand: searchResult.brandName || undefined,
        totalResults: totalResults,
        results: totalResults,
        category: categoryName.value || pageData.value?.metadata?.navigationTitle || undefined
    };

    const titleLabelKey = props.brandSubContent ? 'Brand.SearchPage.SEO.Title' : 'Brand.Page.SEO.Title';
    const title = dictionaryService.get(titleLabelKey, [searchResult.totalHits.toString(), searchResult.brandName || props.brandName]);
    const titleFromPage = pageData.value?.metadata?.seoTitle;
    const titleWithParams = dictionaryService.getWithNamedValues(titleFromPage || titleLabelKey, labelValues, true, !!titleFromPage);
    domService.setTitle(titleWithParams || title);

    const descriptionLabelKey = props.brandSubContent ? 'Brand.SearchPage.SEO.Description' : 'Brand.Page.SEO.Description';
    const description = dictionaryService.get(descriptionLabelKey, [searchResult.totalHits.toString(), searchResult.brandName || props.brandName]);
    const descriptionFromPage = pageData.value?.metadata?.seoDescription;
    const descriptionWithParams = dictionaryService.getWithNamedValues(descriptionFromPage || descriptionLabelKey, labelValues, true, !!descriptionFromPage);
    domService.setDescription(descriptionWithParams || description);
}

function isInCategorySearch() {
    return urlHelper.getCategoryIds().length > 0;
}

watch(() => route, (to, from) => {
    const isSearching = urlHelper.isSearching(to) || urlHelper.isSearching(from);
    if (isSearching) return;
    // changing from one brand page to another
    const didPathChange = !from || (to.path !== from.path);
    if (didPathChange) {
        allowSearch.value = !props.brandSubContent;
        pageData.value = null;
    }

    setup();
}, { immediate: true, deep: true });

function setup() {
    if (props.brandSubContent) {
        pageDataUrl.value = `/${serverContextService.settings?.brandsPage}/${props.brandName}/${props.brandSubContent}`;
    } else {
        pageDataUrl.value = isInCategorySearch()
            ? null
            : `${serverContextService.settings?.brandsPage}?brandName=${encodeURIComponent(props.brandName ?? '')}`;
    }
}

async function onBrandContentPageLoad(receivedPageData: PageData | null) {
    pageData.value = receivedPageData;

    const content = pageData.value?.jsonContent?.content;

    if (props.brandSubContent) {
        brandSubHeadline.value = content?.displayName || pageData.value?.metadata.navigationTitle || pageData.value?.metadata.seoTitle || null;
        if (pageData.value?.metadata.languages) {
            domService.setHreflang(pageData.value.metadata.languages, true, location.href);
        }
    } else {
        brandSubHeadline.value = null;
    }

    botService.setPrerenderRedirectUrlsIfLocationIsWrong(pageData.value?.metadata?.url || null);
    nextTick(() => {
        allowSearch.value = true;
    });
}

const getDefaultCategoryIds = computed((): string[] | null => {
    const content = (pageData.value?.jsonContent?.content) || null;
    return content?.categoryIds || null;
});

const brandNameComputed = computed((): string | null => {
    return pageData.value?.jsonContent?.content?.brandName || null;
});

const hideLeftMenu = computed((): boolean => {
    return pageData.value?.navigation?.hideLeftMenu || false;
});

const showBlocksAndProducts = computed((): boolean => {
    const isInfiniteBrandPage = route.path.indexOf('brands/estuff/infinite') !== -1;
    return !!props.brandSubContent && !isInfiniteBrandPage;
});

const searchTitleComputed = computed((): string | null => {
    return ((props.brandSubContent && brandSubHeadline.value) || brandNameComputed.value) || null;
});
</script>
